import React, { useState } from 'react';
import { supabase } from '../../supabase';
import { Settings as SettingsIcon, Save, MessageSquare } from 'lucide-react';

export default function Settings() {
  const [config, setConfig] = useState({
    slotCount: 20,
    maintenanceDeposit: 50,
    contactEmail: '',
    contactPhone: '',
    businessName: 'Monarch Detailing',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [testPhone, setTestPhone] = useState('(301) 885-9767');
  const [testingSms, setTestingSms] = useState(false);
  const [testSmsResult, setTestSmsResult] = useState(null);

  React.useEffect(() => {
    supabase.from('settings').select('data').eq('id', 'config').single().then(({ data }) => {
      if (data?.data) setConfig(c => ({ ...c, ...data.data }));
      setLoaded(true);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await supabase.from('settings').update({ data: config }).eq('id', 'config');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    setTestSmsResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/send-admin-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ type: 'test', clientPhone: testPhone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setTestSmsResult({ ok: true, message: `Sent to ${testPhone} — check your phone.` });
    } catch (err) {
      setTestSmsResult({ ok: false, message: err.message });
    } finally {
      setTestingSms(false);
    }
  };

  if (!loaded) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-6 max-w-2xl">
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
        <SettingsIcon size={20} /> Settings
      </h2>

      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-5">
        <h3 className="font-semibold text-gray-800">Business Configuration</h3>

        {[
          { key: 'businessName', label: 'Business Name', type: 'text' },
          { key: 'contactEmail', label: 'Contact Email', type: 'email' },
          { key: 'contactPhone', label: 'Contact Phone', type: 'tel' },
          { key: 'slotCount', label: 'Max Maintenance Slots', type: 'number' },
          { key: 'maintenanceDeposit', label: 'Maintenance Deposit ($)', type: 'number' },
        ].map(({ key, label, type }) => (
          <div key={key}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <input
              type={type}
              value={config[key] || ''}
              onChange={e => setConfig(c => ({ ...c, [key]: type === 'number' ? Number(e.target.value) : e.target.value }))}
              className="w-full bg-white text-gray-900 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            />
          </div>
        ))}

        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            saved ? 'bg-emerald-500 text-white' : 'bg-[#C9A84C] text-black hover:bg-[#b8943d]'
          } disabled:opacity-50`}
        >
          <Save size={16} />
          {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Settings'}
        </button>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <MessageSquare size={16} /> Test SMS
        </h3>
        <p className="text-sm text-gray-500">
          Sends a real text through the site's SMS system (Telnyx) to the number below — a quick way to confirm texts are actually going out.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            type="tel"
            value={testPhone}
            onChange={e => setTestPhone(e.target.value)}
            placeholder="(301) 885-9767"
            className="flex-1 min-w-[180px] bg-white text-gray-900 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
          />
          <button
            onClick={handleTestSms}
            disabled={testingSms || !testPhone.trim()}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#C9A84C] text-black hover:bg-[#b8943d] disabled:opacity-50 transition-colors"
          >
            {testingSms ? 'Sending…' : 'Send Test Text'}
          </button>
        </div>
        {testSmsResult && (
          <p className={`text-sm ${testSmsResult.ok ? 'text-emerald-600' : 'text-red-500'}`}>
            {testSmsResult.message}
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-3">Admin Info</h3>
        <p className="text-sm text-gray-600">
          Logged in as admin. Manage users in the Supabase Authentication dashboard.
        </p>
      </div>
    </div>
  );
}
